/**
 * Page 2: account management (`/account`).
 *
 * Profile + identity fingerprint, authorized devices (revoke), team members & invitations
 * (Owner/Admin), and the RunOnly change/error approval queue.
 */

import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useAuth } from "../auth/AuthContext";
import { deleteDevice, inviteMember, resolveApproval } from "../api";

const ROLE_LABEL: Record<string, string> = {
  owner: "Owner",
  admin: "Admin (full read/write)",
  run_only: "Run only (approval required)",
};

export function AccountPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const { account, refreshAccount, isAdmin } = auth;
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<"admin" | "run_only">("run_only");
  const [actionError, setActionError] = useState<string | null>(null);
  const [pending, setPending] = useState<string | null>(null);

  useEffect(() => {
    if (auth.session && !account) void refreshAccount();
  }, [auth.session, account, refreshAccount]);

  const runAction = useCallback(async (key: string, action: () => Promise<unknown>) => {
    setPending(key);
    setActionError(null);
    try {
      await action();
      await refreshAccount();
    } catch (cause) {
      setActionError(cause instanceof Error ? cause.message : "Action failed");
    } finally {
      setPending(null);
    }
  }, [refreshAccount]);

  if (!auth.session) return null;
  const role = auth.session.role ?? "run_only";
  const email = auth.session.email ?? account?.user?.email ?? "";

  return (
    <main className="tj-container max-w-4xl space-y-4 py-6" data-testid="account-page">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-wide text-[var(--tj-muted)]">Account</p>
          <h1 className="serif-heading text-2xl">{account?.account_name ?? "Your workspace"}</h1>
          <p className="text-sm text-[var(--tj-muted)]">
            {email} · {ROLE_LABEL[role] ?? role}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link to="/settings" className="tj-btn tj-btn-secondary inline-block">
            Secrets &amp; sessions
          </Link>
          <button
            type="button"
            className="tj-btn tj-btn-secondary"
            onClick={() => {
              auth.signOut();
              void navigate({ to: "/login" });
            }}
          >
            Sign out
          </button>
        </div>
      </header>

      {actionError ? (
        <p className="tj-badge tj-badge-red block rounded-md p-3" role="alert">
          {actionError}
        </p>
      ) : null}

      <section className="rounded-xl border border-[var(--tj-border)] bg-[var(--tj-surface)] p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="serif-heading text-lg">Identity &amp; vault</h2>
          <span className={`tj-badge ${auth.vaultUnlocked ? "tj-badge-green" : "tj-badge-amber"}`}>
            {auth.vaultUnlocked ? "Vault unlocked" : "Vault locked"}
          </span>
        </div>
        <dl className="mt-3 grid gap-2 text-sm sm:grid-cols-2">
          <div>
            <dt className="text-xs uppercase tracking-wide text-[var(--tj-muted)]">Account ID</dt>
            <dd className="font-mono text-xs break-all">{auth.session.accountId ?? "—"}</dd>
          </div>
          <div>
            <dt className="text-xs uppercase tracking-wide text-[var(--tj-muted)]">Identity fingerprint</dt>
            <dd className="font-mono text-xs break-all">{auth.identityFingerprint ?? "Unlock to derive"}</dd>
          </div>
          <div>
            <dt className="text-xs uppercase tracking-wide text-[var(--tj-muted)]">Enabled apps</dt>
            <dd>{account?.enabled_apps.join(", ") ?? "—"}</dd>
          </div>
          <div>
            <dt className="text-xs uppercase tracking-wide text-[var(--tj-muted)]">Vault subkey</dt>
            <dd className="font-mono text-xs">tensilelabs:vault:taskjinn:v1</dd>
          </div>
        </dl>
        {!auth.vaultUnlocked ? (
          <button
            type="button"
            className="tj-btn tj-btn-primary mt-3"
            disabled={auth.busy}
            onClick={() => void auth.unlockVault()}
          >
            Unlock vault with your Passkey
          </button>
        ) : null}
      </section>

      <section className="rounded-xl border border-[var(--tj-border)] bg-[var(--tj-surface)] p-4">
        <h2 className="serif-heading text-lg">Authorized devices</h2>
        <p className="text-xs text-[var(--tj-muted)]">
          Each device holds a Passkey-wrapped copy of your Master Identity key. Revoking one removes
          its access across all Tensile Labs tools.
        </p>
        {!account?.devices.length ? (
          <p className="mt-3 text-sm text-[var(--tj-muted)]">No devices registered yet.</p>
        ) : (
          <ul className="mt-3 divide-y divide-[var(--tj-border)]">
            {account.devices.map((device) => (
              <li key={device.device_id} className="flex flex-wrap items-center justify-between gap-2 py-2">
                <div>
                  <p className="text-sm font-medium">{device.device_name || device.device_id}</p>
                  <p className="font-mono text-xs text-[var(--tj-muted)]">
                    {device.device_id} · wrapped {new Date(device.created_at).toLocaleDateString()}
                  </p>
                </div>
                <button
                  type="button"
                  className="tj-btn tj-btn-secondary"
                  disabled={pending === device.device_id}
                  onClick={() =>
                    void runAction(device.device_id, () => deleteDevice(device.device_id))
                  }
                >
                  Revoke
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="rounded-xl border border-[var(--tj-border)] bg-[var(--tj-surface)] p-4">
        <h2 className="serif-heading text-lg">Team members</h2>
        <ul className="mt-3 divide-y divide-[var(--tj-border)]">
          {(account?.members ?? []).map((member) => (
            <li key={member.user_id} className="flex flex-wrap items-center justify-between gap-2 py-2">
              <div>
                <p className="text-sm font-medium">{member.display_name || member.email}</p>
                <p className="text-xs text-[var(--tj-muted)]">
                  {member.email} · {member.status}
                </p>
              </div>
              <span className={`tj-badge ${member.role === "run_only" ? "tj-badge-amber" : "tj-badge-green"}`}>
                {ROLE_LABEL[member.role] ?? member.role}
              </span>
            </li>
          ))}
        </ul>

        {isAdmin ? (
          <form
            className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-end"
            onSubmit={(event) => {
              event.preventDefault();
              const target = inviteEmail.trim();
              if (!target) return;
              void runAction("invite", async () => {
                await inviteMember({ email: target, role: inviteRole });
                setInviteEmail("");
              });
            }}
          >
            <label className="flex-1 text-sm font-semibold">
              Invite by email
              <input
                type="email"
                required
                value={inviteEmail}
                onChange={(event) => setInviteEmail(event.target.value)}
                placeholder="teammate@company.com"
                className="mt-1 w-full rounded-lg border border-[var(--tj-border)] bg-[var(--tj-bg)] px-3 py-2 font-normal"
              />
            </label>
            <label className="text-sm font-semibold">
              Role
              <select
                value={inviteRole}
                onChange={(event) => setInviteRole(event.target.value as "admin" | "run_only")}
                className="mt-1 w-full rounded-lg border border-[var(--tj-border)] bg-[var(--tj-bg)] px-3 py-2 font-normal"
              >
                <option value="run_only">Run only</option>
                <option value="admin">Admin</option>
              </select>
            </label>
            <button type="submit" className="tj-btn tj-btn-primary" disabled={pending === "invite"}>
              Send invite
            </button>
          </form>
        ) : (
          <p className="mt-3 text-xs text-[var(--tj-muted)]">
            Only Owners and Admins can invite members.
          </p>
        )}
      </section>

      <section className="rounded-xl border border-[var(--tj-border)] bg-[var(--tj-surface)] p-4">
        <h2 className="serif-heading text-lg">Run-only change &amp; error reviews</h2>
        {!account?.approvals.length ? (
          <p className="mt-2 text-sm text-[var(--tj-muted)]">
            No pending template-change or error-review requests.
          </p>
        ) : (
          <ul className="mt-3 space-y-2">
            {account.approvals.map((request) => (
              <li key={request.request_id} className="rounded-lg border border-[var(--tj-border)] p-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <strong className="text-sm">{request.template_name}</strong>
                  <span
                    className={`tj-badge ${
                      request.status === "approved"
                        ? "tj-badge-green"
                        : request.status === "rejected"
                          ? "tj-badge-red"
                          : "tj-badge-amber"
                    }`}
                  >
                    {request.status}
                  </span>
                </div>
                <p className="mt-1 text-sm text-[var(--tj-muted)]">
                  {request.summary} — by {request.created_by_email}
                </p>
                {isAdmin && request.status === "pending" ? (
                  <div className="mt-2 flex gap-2">
                    <button
                      type="button"
                      className="tj-btn tj-btn-primary"
                      disabled={pending === request.request_id}
                      onClick={() =>
                        void runAction(request.request_id, () =>
                          resolveApproval(request.request_id, "approved"),
                        )
                      }
                    >
                      Approve
                    </button>
                    <button
                      type="button"
                      className="tj-btn tj-btn-secondary"
                      disabled={pending === request.request_id}
                      onClick={() =>
                        void runAction(request.request_id, () =>
                          resolveApproval(request.request_id, "rejected"),
                        )
                      }
                    >
                      Reject
                    </button>
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </section>
    </main>
  );
}
